/* eslint-disable @typescript-eslint/ban-ts-comment */
import { BaseCrudStore } from '@monorepo/controlled/src/stores/base-crud.store';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { runInAction } from 'mobx';
import { BidderCampaignModel } from '../../models/bidder-campaign/bidder-camapign.model';
import { HttpError } from '../../models/http-error.model';
import { BidderApi } from '../../apis/bidder.api';
import { BidderCampaignResponseModel } from '../../models/bidder-campaign/bidder-campaign-response.model';
import { FormError } from '@monorepo/tools/src/lib/models/form-error.model';

const isFieldFilled = (field: number | string | undefined | null): boolean => {
	return field !== undefined && field !== null && field.toString() !== '';
};

const HIGH_BID_REQUIRED_MSG = 'Required';

export class BidderCampaignCrudStore extends BaseCrudStore<
	BidderCampaignResponseModel,
	BidderCampaignModel | undefined,
	BidderCampaignModel | undefined,
	HttpError
> {
	constructor() {
		super({
			// @ts-ignore
			apiLayer: BidderApi,
			model: BidderCampaignResponseModel,
			errorModel: HttpError,
		});
	}

	public isValid(): boolean {
		this.formStore.reset();

		const { campaign_weight, campaign_initial_bids, campaign_bids_new_with_sale, campaign_state_machine_id, campaign_targeting } =
			this.data.campaign;

		const links = campaign_targeting
			?.flatMap(targeting => targeting.campaign_targeting_links)
			.filter(link => link.dest_link?.substring(0, 5) !== 'https');
		const invalidLinks = links ?? [];
		if (invalidLinks.length > 0) {
			this.formStore.addError(new FormError('dest_link', 'Dest link must start with https'));
		}

		if (!campaign_state_machine_id) {
			this.formStore.addError(new FormError('campaign_state_machine_id', 'Please select a state machine ID'));
		}

		if (campaign_weight) {
			if (campaign_weight < 0) {
				this.formStore.addError(new FormError('campaign_weight', 'Minimum value is 0'));
			}
			if (campaign_weight > 100) {
				this.formStore.addError(new FormError('campaign_weight', 'Maximum value is 100'));
			}
		}

		const highMinimalBidFilled = isFieldFilled(campaign_initial_bids?.high_minimal_bid);
		const highMaximumBidFilled = isFieldFilled(campaign_initial_bids?.high_maximum_bid);
		const highRandomBidStepFilled = isFieldFilled(campaign_initial_bids?.high_random_bid_step);

		if (!highMinimalBidFilled) {
			this.formStore.addError(new FormError('campaign_initial_bids.high_minimal_bid', HIGH_BID_REQUIRED_MSG));
		}
		if (!highMaximumBidFilled) {
			this.formStore.addError(new FormError('campaign_initial_bids.high_maximum_bid', HIGH_BID_REQUIRED_MSG));
		}
		if (!highRandomBidStepFilled) {
			this.formStore.addError(new FormError('campaign_initial_bids.high_random_bid_step', HIGH_BID_REQUIRED_MSG));
		}

		if (campaign_initial_bids) {
			if (campaign_initial_bids.random_bid_step <= 0) {
				this.formStore.addError(new FormError('campaign_initial_bids.random_bid_step', 'Minimum value should be greater than 0'));
			}
			if (highRandomBidStepFilled && campaign_initial_bids.high_random_bid_step <= 0) {
				this.formStore.addError(
					new FormError('campaign_initial_bids.high_random_bid_step', 'Minimum value should be greater than 0')
				);
			}
			if (campaign_initial_bids.max_num_Of_clicks_to_first_conv <= 0) {
				this.formStore.addError(
					new FormError('campaign_initial_bids.max_num_Of_clicks_to_first_conv', 'Minimum value should be greater than 0')
				);
			}
			if (campaign_initial_bids.max_loss_per_key <= 0) {
				this.formStore.addError(new FormError('campaign_initial_bids.max_loss_per_key', 'Minimum value should be greater than 0'));
			}
			if (campaign_initial_bids.minimal_bid < 0) {
				this.formStore.addError(new FormError('campaign_initial_bids.minimal_bid', 'Minimum value is 0'));
			}
			if (highMinimalBidFilled && campaign_initial_bids.high_minimal_bid < 0) {
				this.formStore.addError(new FormError('campaign_initial_bids.high_minimal_bid', 'Minimum value is 0'));
			}
			if (campaign_initial_bids.minimal_bid > campaign_initial_bids.maximum_bid) {
				this.formStore.addError(new FormError('campaign_initial_bids.maximum_bid', 'Should be greater than Min Bid'));
			}
			if (
				highMinimalBidFilled &&
				highMaximumBidFilled &&
				campaign_initial_bids.high_minimal_bid > campaign_initial_bids.high_maximum_bid
			) {
				this.formStore.addError(new FormError('campaign_initial_bids.high_maximum_bid', 'Should be greater than Min Bid'));
			}
		}

		const withSaleHighMinimalBidFilled = isFieldFilled(campaign_bids_new_with_sale?.high_minimal_bid);
		const withSaleHighMaximumBidFilled = isFieldFilled(campaign_bids_new_with_sale?.high_maximum_bid);
		const withSaleHighRandomBidStepFilled = isFieldFilled(campaign_bids_new_with_sale?.high_random_bid_step);

		if (!withSaleHighMinimalBidFilled) {
			this.formStore.addError(new FormError('campaign_bids_new_with_sale.high_minimal_bid', HIGH_BID_REQUIRED_MSG));
		}
		if (!withSaleHighMaximumBidFilled) {
			this.formStore.addError(new FormError('campaign_bids_new_with_sale.high_maximum_bid', HIGH_BID_REQUIRED_MSG));
		}
		if (!withSaleHighRandomBidStepFilled) {
			this.formStore.addError(new FormError('campaign_bids_new_with_sale.high_random_bid_step', HIGH_BID_REQUIRED_MSG));
		}

		if (campaign_bids_new_with_sale) {
			const minimalBidFilled = isFieldFilled(campaign_bids_new_with_sale.minimal_bid);
			const maximumBidFilled = isFieldFilled(campaign_bids_new_with_sale.maximum_bid);
			const randomBidStepFilled = isFieldFilled(campaign_bids_new_with_sale.random_bid_step);
			const highMinimalBidFilled = withSaleHighMinimalBidFilled;
			const highMaximumBidFilled = withSaleHighMaximumBidFilled;
			const highRandomBidStepFilled = withSaleHighRandomBidStepFilled;

			const anyFieldFilled =
				minimalBidFilled ||
				maximumBidFilled ||
				randomBidStepFilled ||
				highMinimalBidFilled ||
				highMaximumBidFilled ||
				highRandomBidStepFilled;
			const allFieldsFilled =
				minimalBidFilled &&
				maximumBidFilled &&
				randomBidStepFilled &&
				highMinimalBidFilled &&
				highMaximumBidFilled &&
				highRandomBidStepFilled;

			// If any field is filled, all must be filled
			if (anyFieldFilled && !allFieldsFilled) {
				if (!minimalBidFilled) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.minimal_bid', 'Must fill all fields or no fields at all')
					);
				}
				if (!maximumBidFilled) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.maximum_bid', 'Must fill all fields or no fields at all')
					);
				}
				if (!randomBidStepFilled) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.random_bid_step', 'Must fill all fields or no fields at all')
					);
				}
				if (!highMinimalBidFilled) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.high_minimal_bid', 'Must fill all fields or no fields at all')
					);
				}
				if (!highMaximumBidFilled) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.high_maximum_bid', 'Must fill all fields or no fields at all')
					);
				}
				if (!highRandomBidStepFilled) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.high_random_bid_step', 'Must fill all fields or no fields at all')
					);
				}
			}

			if (allFieldsFilled) {
				if (campaign_bids_new_with_sale.random_bid_step <= 0) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.random_bid_step', 'Minimum value should be greater than 0')
					);
				}
				if (highRandomBidStepFilled && campaign_bids_new_with_sale.high_random_bid_step <= 0) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.high_random_bid_step', 'Minimum value should be greater than 0')
					);
				}
				if (campaign_bids_new_with_sale.minimal_bid < 0) {
					this.formStore.addError(new FormError('campaign_bids_new_with_sale.minimal_bid', 'Minimum value is 0'));
				}
				if (highMinimalBidFilled && campaign_bids_new_with_sale.high_minimal_bid < 0) {
					this.formStore.addError(new FormError('campaign_bids_new_with_sale.high_minimal_bid', 'Minimum value is 0'));
				}
				if (campaign_bids_new_with_sale.minimal_bid > campaign_bids_new_with_sale.maximum_bid) {
					this.formStore.addError(new FormError('campaign_bids_new_with_sale.maximum_bid', 'Should be greater than Min Bid'));
				}
				if (
					highMinimalBidFilled &&
					highMaximumBidFilled &&
					campaign_bids_new_with_sale.high_minimal_bid > campaign_bids_new_with_sale.high_maximum_bid
				) {
					this.formStore.addError(
						new FormError('campaign_bids_new_with_sale.high_maximum_bid', 'Should be greater than Min Bid')
					);
				}
			}
		}

		return this.formStore.getIsValid();
	}

	getCreateFormData() {
		return this.getData().campaign;
	}

	getEditFormData() {
		return this.getData().campaign;
	}

	createInstance(): BidderCampaignResponseModel {
		return new BidderCampaignResponseModel();
	}

	public getBidderCampaign(id: id) {
		if (this.getIsLocalCache()) {
			return Promise.resolve();
		}

		this.setIsLocalCache(true);
		this.setIsLoading(true);

		return BidderApi.getBidderCampaign(id)
			.then(res => {
				const data = new this.model(res);
				const originData = new this.model(res);
				runInAction(() => {
					this.setIsLocalCache(false); //TODO: move to finally
					this.setData(data);
					this.setOriginData(originData);
					this.setIsLoading(false);
				});
				return data;
			})
			.catch(error => {
				if (this.onError) {
					this.onError(error.data);
				}
				runInAction(() => {
					if (this.errorModel) {
						this.setHttpError(new this.errorModel({ ...error.data, httpStatus: error.response?.status }));
					}
					this.setIsLoading(false);
					this.setIsSuccess(false);
				});
			});
	}

	enableCampaign(campaignsId: id): Promise<void> {
		this.setIsLoading(true);
		return BidderApi.enableCampaign(campaignsId)
			.then(() => {
				this.setIsLoading(false);
				this.setIsSuccess(true);
			})
			.catch(() => {
				this.setIsLoading(false);
				this.setIsSuccess(false);
			});
	}

	pauseCampaign(campaignsId: id): Promise<void> {
		this.setIsLoading(true);
		return BidderApi.pauseCampaign(campaignsId)
			.then(() => {
				this.setIsLoading(false);
				this.setIsSuccess(true);
			})
			.catch(() => {
				this.setIsLoading(false);
				this.setIsSuccess(false);
			});
	}
}
